// Modules
import { connect } from 'react-redux';
import Promise from 'bluebird';
import React, { useState } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import createUuid from 'uuid/v4';
import moment from 'moment';

import { CloseButton, SubmitButton, RestoreButton } from 'components/widgets/buttons';
import { CheckboxInput, DateInput } from 'components/forms/form-elements';
import { RoundLoader } from 'components/widgets/state-indicators/loader';
import { UserSelect } from 'components/forms/selects';
import Modal from 'praece-react-bootstrap/lib/Modal';
import { useTaskTemplates } from 'routes/ticket/store/actions';

function buildItem(item, index) {
  if (!item.text) return null;

  const data = {
    status: 'Active',
    uuid: createUuid(),
    order: index,
    description: item.text
  };

  if (item.child) data.subItems = _(item.child).map(buildItem).compact().value();

  return data;
}

function ImportChecklist({ currentUser, disableImporting, ticket, saveTask }) {
  const [selectedDate, setSelectedDate] = useState(moment().toDate());
  const [selectedUser, setSelectedUser] = useState(currentUser.id);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [showPraece, setShowPraece] = useState(false);
  const { templates, isFetching: fetching, refetch } = useTaskTemplates();

  const deselectTemplate = template => setSelectedTemplates(_.without(selectedTemplates, template));
  const selectTemplate = template => setSelectedTemplates(_.union(selectedTemplates, [template]));
  const showPraeceTemplates = show => {
    setShowPraece(show);
    setSelectedTemplates([]);
  };

  const companyTemplates = _.filter(templates, { client_id: Number(ticket.company.id) });
  const praeceTemplates = _.reject(templates, 'client_id');
  const hasCompanyTemplates = !!companyTemplates.length;
  const hasPraeceTemplates = !!praeceTemplates.length;
  const praeceActive = showPraece || !hasCompanyTemplates;
  const activeTemplates = praeceActive ? praeceTemplates : companyTemplates;

  const buildChecklist = () => {
    const checklists = _(selectedTemplates)
      .map(path => _.get(activeTemplates, path))
      .reverse()
      .value();

    Promise.mapSeries(checklists, checklist => {
      const titleParts = _.split(checklist.title, ' ');
      const offset = titleParts[0];
      const add = _.startsWith(offset, '+');
      const subtract = _.startsWith(offset, '-');
      const amount = _(offset).replace('+', '').replace('-', '');
      const increment = _.startsWith(titleParts[1], 'week') ? 'weeks' : 'days';
      let dueDate = selectedDate;

      if (add) dueDate = moment(dueDate).add(amount, increment);
      if (subtract) dueDate = moment(dueDate).subtract(amount, increment);

      const task = {
        dueDate,
        checklist: _(checklist.data).map(buildItem).compact().value(),
        description: checklist.title || '[No Title]',
        assignedTo: [{ id: selectedUser }],
        createdAt: moment().toDate()
      };

      return saveTask(task.description, task);
    })
      .then(() => disableImporting());
  };

  return (
    <Modal keyboard show className="import-task-modal" animation={false}>
      <Modal.Header>
        <Modal.Title>Import Tasks from Praece Know</Modal.Title>
        <div className="d-flex align-items-center justify-content-center">
          <RestoreButton title="Refresh" onClick={refetch} />
          <CloseButton onClick={disableImporting} />
        </div>
      </Modal.Header>
      <Modal.Body className="bg-light pb-0">
        <div>
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <a
                className={classNames('nav-link', { active: !praeceActive, disabled: !hasCompanyTemplates })}
                onClick={() => showPraeceTemplates(false)}
              >
                {hasCompanyTemplates && ticket.company.name}
                {!hasCompanyTemplates && 'No Company Templates'}
              </a>
            </li>
            <li className="nav-item">
              <a
                className={classNames('nav-link', { active: praeceActive, disabled: !hasPraeceTemplates })}
                onClick={() => showPraeceTemplates(true)}
              >
                Praece & Internal
              </a>
            </li>
          </ul>
        </div>
      </Modal.Body>
      <Modal.Body style={{ maxHeight: '50vh', overflow: 'auto' }}>
        { fetching && <RoundLoader color="#34495e" className="page-loader" /> }
          
        { !fetching && activeTemplates.length &&
          <div className="import-task-doc-list">
            {_.map(activeTemplates, (doc, docIndex) =>
              <div className="mb-3" key={docIndex}>
                <p className="mb-1">
                  <a href={doc.url} target="_blank">
                    {praeceActive && `${_.join(doc.breadcrumbs, ' > ')} > ${doc.title}`}
                    {!praeceActive && doc.title}
                  </a>
                </p>
                <div className="import-task-checklist-list">
                  {_.map(doc.checklists, (checklist, checklistIndex) => {
                    const path = `[${docIndex}].checklists[${checklistIndex}]`;
                    const checked = _.includes(selectedTemplates, path);
                    const onChange = checked ? () => deselectTemplate(path) : () => selectTemplate(path);
                    
                    return (
                      <CheckboxInput
                        input={{ value: checked, onChange }}
                        key={checklistIndex}
                        label={checklist.title || '[No Title]'}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        }
      </Modal.Body>
      <Modal.Footer>
        <DateInput input={{ value: selectedDate, onChange: setSelectedDate }} />
        <UserSelect input={{ value: selectedUser, onChange: setSelectedUser }} />
        <SubmitButton content="Import Tasks" onClick={buildChecklist} />
      </Modal.Footer>
    </Modal>
  );
}

// Exports
export default connect((state) => ({
  currentUser: state.auth.currentUser
}))(ImportChecklist);
